import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Sparkles, Zap, Trophy } from "lucide-react";

/**
 * /qf-reveal — Public QF Draw Reveal hype page.
 * Behavior:
 *   - Fetches /api/public/qf/reveal (contains reveal_at + seeds + matchups).
 *   - Before reveal_at: shows a 3-2-1 countdown, teams blurred/hidden.
 *   - At or after reveal_at (or when reveal_at is null): reveals the 4 matchups
 *     one at a time with an animation, then shows the full seed ladder.
 */
export default function QFDrawReveal() {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [revealedCount, setRevealedCount] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: r } = await api.get("/public/qf/reveal");
        if (alive) setData(r);
      } catch { /* noop */ }
    })();
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const target = data?.reveal_at ? new Date(data.reveal_at).getTime() : null;
  const secondsLeft = target ? Math.max(0, Math.floor((target - now) / 1000)) : 0;
  const isRevealTime = !target || secondsLeft === 0;

  // Auto-reveal one card at a time once we're past the countdown.
  useEffect(() => {
    if (!isRevealTime || !data?.matchups?.length) return;
    if (revealedCount >= data.matchups.length) return;
    const t = setTimeout(() => setRevealedCount((c) => c + 1), 900);
    return () => clearTimeout(t);
  }, [isRevealTime, revealedCount, data]);

  if (!data) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-zinc-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#0a1512] to-black text-white overflow-hidden" data-testid="qf-reveal-page">
      {/* subtle grain */}
      <div className="fixed inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 0.5px, transparent 0.5px), radial-gradient(circle at 80% 60%, #fff 0.5px, transparent 0.5px)", backgroundSize: "24px 24px" }} />

      <div className="relative z-10 max-w-6xl mx-auto p-6 md:p-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full border border-[#F4B400]/40 bg-[#F4B400]/10 text-[#F4B400] text-[11px] uppercase tracking-[0.3em] mb-4">
            <Sparkles className="w-3 h-3" /> Season 03 · Playoff Draw
          </div>
          <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tight">
            Quarterfinal Draw
          </h1>
          <p className="text-zinc-400 mt-2 text-sm">The 8 who made it. The 4 fights ahead.</p>
        </div>

        {!isRevealTime && target ? (
          <Countdown seconds={secondsLeft} target={new Date(target)} />
        ) : !data.ready ? (
          <Card className="p-8 bg-[#0f1a15] border-white/10 text-center max-w-2xl mx-auto">
            <div className="text-zinc-400 text-sm">
              The draw is not ready yet — group stage is still in progress.
            </div>
          </Card>
        ) : (
          <>
            {/* Matchups reveal grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10" data-testid="qf-reveal-matchups">
              {(data.matchups || []).map((m, i) => (
                <MatchupCard key={m.label} matchup={m} revealed={i < revealedCount} index={i} />
              ))}
            </div>

            {/* Seed ladder — appears once all matchups are revealed */}
            {revealedCount >= data.matchups.length && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-700" data-testid="qf-reveal-seed-ladder">
                <div className="text-center mb-4">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">
                    <Trophy className="w-3 h-3" /> The Path To Glory
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {(data.seeds || []).map((s) => (
                    <div key={s.seed} className={`p-3 rounded-lg border ${
                      s.seed <= 3 ? "bg-[#F4B400]/10 border-[#F4B400]/40" :
                      s.seed <= 6 ? "bg-[#00A86B]/10 border-[#00A86B]/40" :
                      "bg-[#E32424]/10 border-[#E32424]/40"
                    }`}>
                      <div className="flex items-baseline justify-between mb-1">
                        <div className="font-anton text-2xl leading-none">#{s.seed}</div>
                        <div className="text-[9px] uppercase tracking-widest text-zinc-400">{s.source}</div>
                      </div>
                      <div className="text-xs font-bold text-white truncate">{s.team_name}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Countdown({ seconds, target }) {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const showFinalCountdown = seconds <= 3 && seconds > 0;

  return (
    <div className="text-center py-8" data-testid="qf-reveal-countdown">
      <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-6">Draw goes live in</div>
      {showFinalCountdown ? (
        <div key={seconds} className="font-anton text-[220px] leading-none text-[#F4B400] animate-in zoom-in-50 duration-500" data-testid="qf-reveal-final-countdown">
          {seconds}
        </div>
      ) : (
        <div className="flex items-center justify-center gap-6 flex-wrap">
          {[
            { label: "Days", value: days },
            { label: "Hours", value: hours },
            { label: "Mins", value: mins },
            { label: "Secs", value: secs },
          ].map((u) => (
            <div key={u.label} className="flex flex-col items-center">
              <div className="font-anton text-6xl md:text-8xl leading-none tabular-nums text-white">
                {String(u.value).padStart(2, "0")}
              </div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mt-2">{u.label}</div>
            </div>
          ))}
        </div>
      )}
      <div className="text-xs text-zinc-500 mt-8">
        {target.toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
      </div>
    </div>
  );
}

function MatchupCard({ matchup, revealed, index }) {
  return (
    <Card
      className={`p-5 bg-[#0f1a15] border transition-all duration-700 ${
        revealed
          ? "border-[#F4B400]/40 shadow-[0_0_40px_-10px_rgba(244,180,0,0.4)] scale-100 opacity-100"
          : "border-white/5 opacity-40 scale-95 blur-sm"
      }`}
      style={{ transitionDelay: revealed ? `${index * 100}ms` : "0ms" }}
      data-testid={`qf-reveal-matchup-${matchup.label}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <Zap className={`w-4 h-4 ${revealed ? "text-[#F4B400]" : "text-zinc-600"}`} />
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#F4B400]">{matchup.label}</div>
      </div>
      <TeamRow seed={matchup.seed_a} name={revealed ? matchup.team_a?.team_name : "??????"} sub={matchup.team_a?.source} revealed={revealed} />
      <div className="text-center text-[10px] uppercase tracking-[0.3em] text-zinc-500 my-2 font-bold">vs</div>
      <TeamRow seed={matchup.seed_b} name={revealed ? matchup.team_b?.team_name : "??????"} sub={matchup.team_b?.source} revealed={revealed} />
    </Card>
  );
}

function TeamRow({ seed, name, sub, revealed }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${
      seed <= 3 ? "border-[#F4B400]/30 bg-[#F4B400]/5" :
      seed <= 6 ? "border-[#00A86B]/30 bg-[#00A86B]/5" :
      "border-[#E32424]/30 bg-[#E32424]/5"
    }`}>
      <div className={`w-10 text-center font-anton text-2xl ${
        seed <= 3 ? "text-[#F4B400]" : seed <= 6 ? "text-[#00A86B]" : "text-[#E32424]"
      }`}>#{seed}</div>
      <div className="flex-1 min-w-0">
        <div className={`text-base font-bold truncate ${revealed ? "text-white" : "text-zinc-500"}`}>{name}</div>
        {sub && revealed && <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{sub}</div>}
      </div>
    </div>
  );
}
